/**
 * Hook compartido para cargar la watchlist de UIDs flagged.
 * Usa useSyncExternalStore para evitar problemas con React StrictMode +
 * cache global: todos los consumidores siempre ven el último snapshot.
 *
 * Endpoint: GET /api/v2/brain/flagged-uids
 *
 * Devuelve:
 *   - flaggedMap: Map<uid, {severity, partner_uids, pair_count, verdicts_in}>
 *   - loading, error
 *   - refresh(): invalida cache y re-fetchea
 *   - computeRowFlag(player_uids): { hasFlag, isMatch, maxSeverity, flaggedPlayers }
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CACHE_TTL_MS = 5 * 60 * 1000;

const SEVERITY_RANK = { weak: 1, medium: 2, high: 3 };
const SEVERITY_FROM_RANK = { 1: "weak", 2: "medium", 3: "high" };

// Store externo con pubsub
const _store = {
  flaggedMap: new Map(),
  loading: true,
  error: null,
  fetchedAt: 0,
  inflight: null,
  listeners: new Set(),
  emit() {
    this.listeners.forEach((l) => l());
  },
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  },
  getSnapshot() {
    return _snapshot;
  },
};

let _snapshot = {
  flaggedMap: _store.flaggedMap,
  loading: _store.loading,
  error: _store.error,
};

function _updateSnapshot() {
  _snapshot = {
    flaggedMap: _store.flaggedMap,
    loading: _store.loading,
    error: _store.error,
  };
  _store.emit();
}

function _isCacheFresh() {
  return _store.fetchedAt > 0 && Date.now() - _store.fetchedAt < CACHE_TTL_MS;
}

function _fetch(force = false) {
  if (!force && _isCacheFresh()) return Promise.resolve(_store.flaggedMap);
  if (_store.inflight) return _store.inflight;

  _store.loading = true;
  _store.error = null;
  _updateSnapshot();

  _store.inflight = axios
    .get(`${API}/api/v2/brain/flagged-uids`)
    .then((res) => {
      const list = res.data?.uids || [];
      const m = new Map();
      for (const r of list) {
        m.set(r.uid, {
          severity: r.severity,
          pair_count: r.pair_count,
          partner_uids: r.partner_uids || [],
          verdicts_in: r.verdicts_in || [],
        });
      }
      _store.flaggedMap = m;
      _store.loading = false;
      _store.error = null;
      _store.fetchedAt = Date.now();
      _store.inflight = null;
      _updateSnapshot();
      return m;
    })
    .catch((err) => {
      _store.loading = false;
      _store.error = err?.message || "Error cargando watchlist";
      _store.inflight = null;
      _updateSnapshot();
      return _store.flaggedMap;
    });
  return _store.inflight;
}

function _maxSeverity(severities) {
  if (!severities?.length) return null;
  let rank = 0;
  for (const s of severities) rank = Math.max(rank, SEVERITY_RANK[s] || 0);
  return rank > 0 ? SEVERITY_FROM_RANK[rank] : null;
}

export function useFlaggedUids() {
  const snap = useSyncExternalStore(
    _store.subscribe.bind(_store),
    _store.getSnapshot.bind(_store),
    _store.getSnapshot.bind(_store)
  );

  useEffect(() => {
    if (!_isCacheFresh() && !_store.inflight) {
      _fetch(false);
    }
  }, []);

  const refresh = useCallback(() => _fetch(true), []);

  const computeRowFlag = useCallback(
    (player_uids) => {
      const empty = {
        hasFlag: false,
        isMatch: false,
        maxSeverity: null,
        flaggedPlayers: [],
        matchPairs: [],
      };
      const m = snap.flaggedMap;
      if (!player_uids?.length || m.size === 0) return empty;

      const flaggedPlayers = [];
      for (const uid of player_uids) {
        const entry = m.get(uid);
        if (entry) flaggedPlayers.push({ uid, ...entry });
      }
      if (flaggedPlayers.length === 0) return empty;

      const matchPairs = [];
      for (let i = 0; i < flaggedPlayers.length; i++) {
        for (let j = i + 1; j < flaggedPlayers.length; j++) {
          const a = flaggedPlayers[i];
          const b = flaggedPlayers[j];
          if (a.partner_uids.includes(b.uid)) {
            matchPairs.push([a.uid, b.uid]);
          }
        }
      }
      const maxSev = _maxSeverity(flaggedPlayers.map((p) => p.severity));
      return {
        hasFlag: true,
        isMatch: matchPairs.length > 0,
        maxSeverity: maxSev,
        flaggedPlayers,
        matchPairs,
      };
    },
    [snap.flaggedMap]
  );

  return {
    flaggedMap: snap.flaggedMap,
    loading: snap.loading,
    error: snap.error,
    refresh,
    computeRowFlag,
  };
}

export const ROW_TINTS = {
  match: "#fecaca",
  high: "#fee2e2",
  medium: "#ffedd5",
  weak: "#fef9c3",
};

export const SEVERITY_COLORS = {
  high: "#dc2626",
  medium: "#f97316",
  weak: "#eab308",
};

export const SEVERITY_LABELS = {
  high: "Alta",
  medium: "Media",
  weak: "Débil",
};

export function severityStarColor(sev) {
  return SEVERITY_COLORS[sev] || "#94a3b8";
}

export function rowTintForFlag(flag) {
  if (!flag?.hasFlag) return null;
  if (flag.isMatch) return ROW_TINTS.match;
  if (flag.maxSeverity === "high") return ROW_TINTS.high;
  if (flag.maxSeverity === "medium") return ROW_TINTS.medium;
  if (flag.maxSeverity === "weak") return ROW_TINTS.weak;
  return null;
}
